import { Shield, Sparkles, Truck, Package, Zap } from "lucide-react";
import type { SQDIPSummary } from "../api/useSQDIPDashboard";

type HealthStatus = "green" | "yellow" | "red";

interface SummaryStripProps {
  summary: SQDIPSummary;
  onSelect: (dimension: string) => void;
}

const healthDotColor: Record<HealthStatus, string> = {
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
};

const dimensions = [
  { key: "safety", label: "Safety", icon: Shield, unit: "", suffix: " breaches", borderColor: "border-red-200 dark:border-red-800/40", bgColor: "bg-red-50 dark:bg-red-900/20", textColor: "text-red-700 dark:text-red-300" },
  { key: "quality", label: "Quality", icon: Sparkles, unit: "%", suffix: " rework", borderColor: "border-blue-200 dark:border-blue-800/40", bgColor: "bg-blue-50 dark:bg-blue-900/20", textColor: "text-blue-700 dark:text-blue-300" },
  { key: "delivery", label: "Delivery", icon: Truck, unit: "%", suffix: " completion", borderColor: "border-green-200 dark:border-green-800/40", bgColor: "bg-green-50 dark:bg-green-900/20", textColor: "text-green-700 dark:text-green-300" },
  { key: "inventory", label: "Inventory", icon: Package, unit: "", suffix: " open", borderColor: "border-yellow-200 dark:border-yellow-800/40", bgColor: "bg-yellow-50 dark:bg-yellow-900/20", textColor: "text-yellow-700 dark:text-yellow-300" },
  { key: "productivity", label: "Productivity", icon: Zap, unit: "%", suffix: " utilization", borderColor: "border-purple-200 dark:border-purple-800/40", bgColor: "bg-purple-50 dark:bg-purple-900/20", textColor: "text-purple-700 dark:text-purple-300" },
] as const;

export function SummaryStrip({ summary, onSelect }: SummaryStripProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {dimensions.map((dim) => {
        const data = summary[dim.key as keyof SQDIPSummary];
        const Icon = dim.icon;
        return (
          <button
            key={dim.key}
            onClick={() => onSelect(dim.key)}
            className={`border-2 ${dim.borderColor} ${dim.bgColor} rounded-lg p-3 text-center cursor-pointer hover:shadow-md transition-shadow`}
          >
            <div className={`text-xs font-semibold ${dim.textColor} flex items-center justify-center gap-1`}>
              <Icon className="h-3.5 w-3.5" />
              {dim.label}
            </div>
            <div className={`text-xl font-bold ${dim.textColor} mt-1`}>
              {data.headline}{dim.unit}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{dim.suffix}</div>
            <div className={`w-2 h-2 rounded-full ${healthDotColor[data.health]} mx-auto mt-1.5`} />
          </button>
        );
      })}
    </div>
  );
}
